import type { Location } from '@local/common-lib';
import createServiceFactory from '@mapbox/mapbox-sdk/services/geocoding.js';
import {
  AddLocation as AddLocationIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import type { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useGeolocated } from 'react-geolocated';

interface Props<ID, T> {
  formik: FormikProps<T>;
  id: ID;
  label?: string;
  name?: string;
}

export function FormikLocationAddressField<
  ID extends string,
  T extends { [key in ID]?: Location },
>({ formik, id, label, name }: Props<ID, T>): JSX.Element {
  const [address, setAddress] = useState('');
  const [foundAddress, setFoundAddress] = useState('');
  const geocoding = (import.meta.env.VITE_APP_MAPBOX_TOKEN as string)
    ? createServiceFactory({
        accessToken: import.meta.env.VITE_APP_MAPBOX_TOKEN as string,
      })
    : undefined;

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,
      },
      userDecisionTimeout: 10000,
    });

  // biome-ignore lint/correctness/useExhaustiveDependencies: works, don't want to mess with it.
  useEffect(() => {
    if (!coords || !coords.latitude || !coords.longitude || !geocoding) return;

    geocoding
      .reverseGeocode({
        limit: 1,
        query: [coords.longitude, coords.latitude],
      })
      .send()
      .then((result) => {
        setFoundAddress(result.body.features[0].place_name);
        if (!address && result.body.features && result.body.features.length) {
          setAddress(result.body.features[0].place_name);
        }
        return;
      })
      .catch(console.log);
  }, [coords]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: works, don't want to mess with it.
  useEffect(() => {
    if (
      !isGeolocationAvailable ||
      !isGeolocationEnabled ||
      !coords ||
      !coords.latitude ||
      !coords.longitude
    ) {
      formik.setFieldValue(id, { address: address || foundAddress });
      return;
    }
    const location: Location = {
      address:
        address ||
        foundAddress ||
        `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`,
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
    formik.setFieldValue(id, location);
  }, [coords, isGeolocationAvailable, isGeolocationEnabled, address]);

  if (!isGeolocationAvailable) {
    return (
      <TextField
        disabled={formik.isSubmitting}
        error={formik.touched[id] && Boolean(formik.errors[id])}
        fullWidth
        helperText="Your browser does not support Geolocation"
        label={label}
        name={name}
        onBlur={formik.handleBlur}
        onChange={(event) => setAddress(event.target.value)}
        value={address}
      />
    );
  }

  if (!isGeolocationEnabled) {
    return (
      <TextField
        disabled={formik.isSubmitting}
        error={formik.touched[id] && Boolean(formik.errors[id])}
        fullWidth
        helperText="Geolocation is not enabled"
        label={label}
        name={name}
        onBlur={formik.handleBlur}
        onChange={(event) => setAddress(event.target.value)}
        value={address}
      />
    );
  }

  if (!coords) {
    return (
      <TextField
        disabled
        error={formik.touched[id] && Boolean(formik.errors[id])}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CircularProgress size="1rem" />
            </InputAdornment>
          ),
        }}
        label={label}
        name={name}
        value="Loading location"
      />
    );
  }

  return (
    <TextField
      disabled={formik.isSubmitting}
      error={formik.touched[id] && Boolean(formik.errors[id])}
      fullWidth
      helperText={`${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`}
      InputProps={
        address
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="clear" onClick={() => setAddress('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }
          : foundAddress
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="use location"
                      onClick={() => setAddress(foundAddress)}
                    >
                      <AddLocationIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : {}
      }
      label={label}
      name={name}
      onBlur={formik.handleBlur}
      onChange={(event) => setAddress(event.target.value)}
      placeholder={foundAddress}
      value={address}
    />
  );
}
